import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import { useIsFetching } from '@tanstack/react-query'
import KNAdvancedDropdown from 'components/KNAdvancedDropdown/KNAdvancedDropdown'
import KNDatePickerNext from 'components/KNDatePicker/KNDatePickerNext'
import { useQuotesView } from 'context/QuotesViewContext'
import { addDays, endOfDay, startOfDay, subDays } from 'date-fns'
import { type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { QUOTES_QUERY_KEY } from './Quotes.service'
import { QuoteStatus } from './Quotes.types'

const QuoteFilters = (): ReactElement => {
  const { t } = useTranslation()
  const { filters, filtersDirty, setFilters, resetFilters } = useQuotesView()
  const isFetching = useIsFetching({ queryKey: [QUOTES_QUERY_KEY] })

  return (
    <Box sx={{ position: 'relative' }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={filters.keywords ?? []}
            onChange={(_event, newValue): void => {
              setFilters({
                keywords: newValue,
              })
            }}
            renderValue={(tagValue, getTagProps): ReactElement[] =>
              tagValue.map((option, index) => (
                // eslint-disable-next-line react/jsx-key
                <Chip {...getTagProps({ index })} variant="outlined" size="small" label={option} />
              ))
            }
            renderInput={(params): ReactElement => (
              <TextField {...params} label={t('screens.cs.quotes.filters.search')} />
            )}
            disabled={isFetching > 0}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <KNAdvancedDropdown
            label={t('screens.cs.quotes.filters.status')}
            options={Object.values(QuoteStatus).map((status) => ({
              value: status,
              label: t(`screens.cs.quotes.statuses.${status}`),
            }))}
            value={filters.status}
            onChange={(newValue): void => {
              setFilters({
                status: newValue as QuoteStatus[],
              })
            }}
            onDelete={(value) => {
              setFilters({
                status: filters.status.filter((status) => status !== value),
              })
            }}
            multiple
            disabled={isFetching > 0}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <KNDatePickerNext
            mode="range"
            value={{
              from: filters.fromDate,
              to: filters.toDate,
            }}
            minDate={startOfDay(subDays(new Date(), 90))}
            maxDate={endOfDay(addDays(new Date(), 90))}
            onChange={(value): void => {
              setFilters({
                fromDate: value?.from,
                toDate: value?.to,
              })
            }}
            labelFrom={t('screens.cs.quotes.filters.date_from')}
            labelTo={t('screens.cs.quotes.filters.date_to')}
          />
        </Grid>
      </Grid>
      {filtersDirty && (
        <Button
          size="small"
          variant="text"
          color="primary"
          onClick={resetFilters}
          sx={{
            padding: 0,
            minHeight: 'auto',
          }}
        >
          {t('general.reset_filters')}
        </Button>
      )}
    </Box>
  )
}

export default QuoteFilters

